// apps/web/src/components/chat/forms/TrialUploadForm.tsx
import { Button, Typography, Box, Alert, LinearProgress } from '@mui/material';
import { useState } from 'react';
import { getSupabaseClient } from '@lingaai/supabase-client';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

interface TrialUploadFormProps {
  data: Record<string, never>;
  tenantId: string;
  onSuccess: (result: { uploadedFile: UploadedFileMetadata }) => void;
  onClose: () => void;
}

export interface UploadedFileMetadata {
  fileName: string;
  filePath: string;
  fileSize: number;
  mimeType: string;
}

export function TrialUploadForm({ tenantId, onSuccess, onClose }: TrialUploadFormProps) {
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('');
  const supabase = getSupabaseClient();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];

      // Validate file type
      const validTypes = [
        'application/pdf',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ];

      if (!validTypes.includes(selectedFile.type)) {
        setError('Please upload a PDF or Excel file');
        return;
      }

      // Validate file size (max 50MB)
      const maxSize = 50 * 1024 * 1024;
      if (selectedFile.size > maxSize) {
        setError('File size must be less than 50MB');
        return;
      }

      setFile(selectedFile);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Upload file to documents bucket
      setStatus('Uploading your report...');
      const timestamp = Date.now();
      const filePath = `${tenantId}/trial/${timestamp}-${file.name}`;

      const { error: uploadError } = await supabase.storage.from('documents').upload(filePath, file);

      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      setStatus('File uploaded! Analyzing...');

      // Return file metadata to agent via onSuccess
      // The agent will call setup_trial_from_document with this data
      const uploadedFile: UploadedFileMetadata = {
        fileName: file.name,
        filePath: filePath,
        fileSize: file.size,
        mimeType: file.type,
      };

      onSuccess({ uploadedFile });
    } catch (err) {
      console.error('Trial upload error:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload file');
      setIsProcessing(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];

      // Validate file type
      const validTypes = [
        'application/pdf',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ];

      if (!validTypes.includes(droppedFile.type)) {
        setError('Please upload a PDF or Excel file');
        return;
      }

      setFile(droppedFile);
      setError(null);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Try Linga AI
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload a past quarterly report (PDF or Excel) and watch our AI automatically extract and standardize your
        metrics.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        sx={{
          mb: 3,
          p: 3,
          border: '2px dashed',
          borderColor: file ? 'primary.main' : 'grey.300',
          borderRadius: 2,
          textAlign: 'center',
          cursor: isProcessing ? 'not-allowed' : 'pointer',
          bgcolor: file ? 'primary.50' : 'grey.50',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'primary.50',
          },
        }}
      >
        <input
          type="file"
          accept=".pdf,.xlsx,.xls"
          onChange={handleFileChange}
          style={{ display: 'none' }}
          id="trial-file-upload"
          disabled={isProcessing}
        />
        <label htmlFor="trial-file-upload" style={{ cursor: isProcessing ? 'not-allowed' : 'pointer' }}>
          <CloudUploadIcon sx={{ fontSize: 48, color: file ? 'primary.main' : 'grey.400', mb: 1 }} />
          <Typography variant="body1" color={file ? 'primary.main' : 'text.secondary'}>
            {file ? file.name : 'Drop your file here or click to browse'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Supports PDF, Excel (.xlsx, .xls) - Max 50MB
          </Typography>
        </label>
      </Box>

      {isProcessing && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {status}
          </Typography>
          <LinearProgress />
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={onClose} disabled={isProcessing}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleUpload} disabled={!file || isProcessing}>
          {isProcessing ? 'Uploading...' : 'Analyze Document'}
        </Button>
      </Box>
    </Box>
  );
}
